/**
 * Password reset: reading the link a user comes back on, and deciding what the
 * app shows first.
 *
 * ─── HOW A RESET LINK ARRIVES ───────────────────────────────────────────────
 * supabase-js (2.105) defaults to the IMPLICIT flow, so the email link lands
 * with its tokens in the URL fragment:
 *
 *   https://app.designstudiohq.com/#access_token=…&refresh_token=…&type=recovery
 *
 * or, when the link is stale or already used:
 *
 *   https://app.designstudiohq.com/#error=access_denied&error_code=otp_expired&error_description=…
 *
 * Implicit is kept on purpose. PKCE stores a verifier in the browser that
 * REQUESTED the reset, so asking on a laptop and opening the email on a phone
 * would fail — a normal way for a designer to use this.
 *
 * ─── WHY WE READ THE URL OURSELVES ──────────────────────────────────────────
 * The library consumes those tokens while it starts up, clears the fragment,
 * and announces PASSWORD_RECOVERY via setTimeout(0). main.ts only subscribes
 * to auth events AFTER startup, so relying on that event means racing it — the
 * "callback fires before you're listening" trap in CLAUDE.md. Reading the
 * fragment synchronously, before anything touches the Supabase client, has no
 * race to lose.
 */

export type AuthRedirect =
  | { kind: 'none' }
  | { kind: 'recovery' }
  // Phase 24: an invitation link. Same shape as a reset link — the person is
  // signed in by it and must choose a password — with type=invite.
  | { kind: 'invite' }
  // An email link on OUR domain carrying a one-time token_hash (the branded
  // templates in supabase/templates/). Nothing is spent until the person
  // presses Continue — see confirmCopy().
  | { kind: 'confirm'; tokenHash: string; purpose: PasswordPurpose }
  | { kind: 'error'; expired: boolean; message: string };

/** Why someone is on the set-password page. */
export type PasswordPurpose = 'recovery' | 'invite';

/**
 * Classify a URL fragment. App routes (`#/projects/…`) and portal links
 * (`#/share/…`) are 'none': they start with `#/`, never with parameters.
 */
export function readAuthRedirect(hash: string): AuthRedirect {
  const frag = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!frag || frag.startsWith('/')) return { kind: 'none' };

  const p = new URLSearchParams(frag);
  if (p.get('error') || p.get('error_code')) {
    const code = p.get('error_code') ?? '';
    return {
      kind: 'error',
      // Supabase reports both a stale link and a reused one as otp_expired.
      expired: code === 'otp_expired',
      message: p.get('error_description') ?? 'That link could not be used.',
    };
  }
  const tokenHash = p.get('token_hash');
  const type = p.get('type');
  if (tokenHash && (type === 'recovery' || type === 'invite')) {
    return { kind: 'confirm', tokenHash, purpose: type };
  }
  if (p.get('type') === 'recovery' && p.get('access_token')) return { kind: 'recovery' };
  if (p.get('type') === 'invite' && p.get('access_token')) return { kind: 'invite' };
  return { kind: 'none' };
}

export type BootMode = 'normal' | 'update-password' | 'reset-link-failed' | 'confirm-link';

/**
 * What to show first.
 *
 * `recoveryPending` covers a reload of the set-password page. The library has
 * already cleared the fragment by then, so without the flag a reload would
 * drop the user into the app still holding their OLD password — easy to
 * mistake for "the change worked".
 */
export function bootMode(
  redirect: AuthRedirect,
  hasSession: boolean,
  recoveryPending: boolean,
): BootMode {
  if (redirect.kind === 'error') return 'reset-link-failed';
  // Whatever session exists, a fresh email link wins: the person clicked it
  // to set a password now, possibly on a browser someone else is signed into.
  if (redirect.kind === 'confirm') return 'confirm-link';
  if (redirect.kind === 'recovery' || redirect.kind === 'invite') {
    // Tokens in the link but no session came of them: treat as a dead link
    // rather than silently showing the sign-in page.
    return hasSession ? 'update-password' : 'reset-link-failed';
  }
  if (recoveryPending && hasSession) return 'update-password';
  return 'normal';
}

/**
 * What to tell someone whose reset link didn't work, shown above the
 * request-a-new-link form. Deliberately not Supabase's own description, which
 * is written for developers ("Email link is invalid or has expired").
 */
export function resetFailureNotice(redirect: AuthRedirect): string {
  if (redirect.kind === 'error' && redirect.expired) {
    return 'That reset link has expired or was already used. Enter your email and we’ll send a new one.';
  }
  return 'That reset link didn’t work. Enter your email and we’ll send a new one.';
}

/** Where the reset email sends the user back to: the address they asked from. */
export function resetRedirectUrl(origin: string): string {
  return `${origin.replace(/\/+$/, '')}/`;
}

export const MIN_PASSWORD_LENGTH = 8;

/**
 * An error to show, or null when the new password is acceptable.
 *
 * The UI check is a courtesy. Supabase enforces its own minimum, which should
 * be raised to match in Authentication → Policies — otherwise a direct API
 * call could still set a shorter one.
 */
export function validateNewPassword(password: string, confirm: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (password.trim() === '') return 'A password cannot be only spaces.';
  if (password !== confirm) return 'The two passwords don’t match.';
  return null;
}

/** sessionStorage key marking a reset in progress. See bootMode(). */
export const RECOVERY_PENDING_KEY = 'tsd:recovery-pending';

/**
 * Read the pending flag. It holds the purpose ('recovery' | 'invite') so a
 * reload of the welcome page stays a welcome page; '1' is what builds before
 * Phase 24 wrote, and still means a reset.
 */
export function pendingPurpose(stored: string | null): PasswordPurpose | null {
  if (stored === 'invite') return 'invite';
  if (stored === 'recovery' || stored === '1') return 'recovery';
  return null;
}

/** The set-password page's words, by why the person is there. */
export function setPasswordCopy(purpose: PasswordPurpose): {
  title: string; intro: string | null; submit: string; cancel: string;
} {
  return purpose === 'invite'
    ? {
        title: 'Welcome — choose a password',
        intro: 'You’ve been invited to Design Studio HQ. Choose a password to finish setting up your account.',
        submit: 'Save password and continue',
        cancel: 'Not now — sign out',
      }
    : {
        title: 'Set a new password',
        intro: null,
        submit: 'Save new password',
        cancel: 'Cancel and sign out',
      };
}

/**
 * The page an emailed link opens on, before anything is spent.
 *
 * WHY A CLICK: company mail filters (Outlook's Safe Links, several
 * gateways) open every link in an email to scan it. A link that signs you in
 * on load is used up by the scanner before the person sees it — they then get
 * "link expired" on a link they never clicked. Requiring a button press means
 * only a human spends it.
 */
export function confirmCopy(purpose: PasswordPurpose): { title: string; intro: string; button: string } {
  return purpose === 'invite'
    ? {
        title: 'You’re invited',
        intro: 'You’ve been invited to Design Studio HQ. Continue to choose a password and finish setting up your account.',
        button: 'Continue',
      }
    : {
        title: 'Reset your password',
        intro: 'Continue to choose a new password for your account.',
        button: 'Continue',
      };
}

/**
 * What to say when an emailed link can't be used, above the form that sends a
 * fresh one. An invited person has an account already, so the same "send me a
 * link" form works for them too.
 */
export function linkFailureNotice(purpose: PasswordPurpose, err: unknown): string {
  const code = (err as { code?: string } | null)?.code ?? '';
  const expired = code === 'otp_expired' || /expired|invalid/i.test((err as { message?: string } | null)?.message ?? '');
  if (purpose === 'invite') {
    return expired
      ? 'That invitation link has expired or was already used. Enter your email and we’ll send a link to choose your password.'
      : 'That invitation link didn’t work. Enter your email and we’ll send a link to choose your password.';
  }
  return resetFailureNotice({ kind: 'error', expired, message: '' });
}
